import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, PoundSterling } from 'lucide-react';
import { EPSReconciliation } from '@/types/payroll-validator';

interface EPSReconciliationCardProps {
  reconciliation: EPSReconciliation;
}

export const EPSReconciliationCard: React.FC<EPSReconciliationCardProps> = ({
  reconciliation,
}) => {
  const formatCurrency = (value: string | number) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return `£${numValue.toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const totalVariance = parseFloat(reconciliation.total_variance);
  const hasIssues = reconciliation.under_claimed || reconciliation.over_claimed;

  return (
    <Card className="border-l-4 border-l-purple-500">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl flex items-center gap-2">
              <PoundSterling className="h-5 w-5" />
              EPS Statutory Payments Reconciliation
            </CardTitle>
            <CardDescription>
              Period: {formatDate(reconciliation.eps_period_start)} to{' '}
              {formatDate(reconciliation.eps_period_end)}
            </CardDescription>
          </div>
          {!hasIssues && (
            <Badge className="bg-green-100 text-green-800 border-green-300">
              <CheckCircle className="h-3 w-3 mr-1" />
              Matched
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Summary Alert */}
        {hasIssues && (
          <Alert className={totalVariance > 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}>
            <AlertTriangle className={`h-4 w-4 ${totalVariance > 0 ? 'text-green-600' : 'text-red-600'}`} />
            <AlertTitle className={totalVariance > 0 ? 'text-green-900' : 'text-red-900'}>
              {reconciliation.under_claimed && 'You may have under-claimed statutory payments'}
              {reconciliation.over_claimed && 'You may have over-claimed statutory payments'}
            </AlertTitle>
            <AlertDescription className={totalVariance > 0 ? 'text-green-800' : 'text-red-800'}>
              {reconciliation.under_claimed && (
                <>
                  You could have reclaimed an additional{' '}
                  <strong>{formatCurrency(reconciliation.under_claimed_amount || '0')}</strong> from HMRC.
                </>
              )}
              {reconciliation.over_claimed && (
                <>
                  You may have over-claimed{' '}
                  <strong>{formatCurrency(reconciliation.over_claimed_amount || '0')}</strong>.
                  HMRC may request repayment.
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Totals Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <p className="text-sm text-blue-700 font-medium mb-1">EPS Claimed</p>
              <p className="text-2xl font-bold text-blue-900">
                {formatCurrency(reconciliation.eps_total_claimed)}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <p className="text-sm text-purple-700 font-medium mb-1">Pay Data Total</p>
              <p className="text-2xl font-bold text-purple-900">
                {formatCurrency(reconciliation.pay_data_total)}
              </p>
            </CardContent>
          </Card>

          <Card className={totalVariance !== 0 ? (totalVariance > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200') : 'bg-gray-50 border-gray-200'}>
            <CardContent className="p-4">
              <p className="text-sm font-medium mb-1 flex items-center gap-1">
                Variance
                {totalVariance > 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-600" />
                ) : totalVariance < 0 ? (
                  <TrendingDown className="h-4 w-4 text-red-600" />
                ) : (
                  <CheckCircle className="h-4 w-4 text-gray-600" />
                )}
              </p>
              <p className={`text-2xl font-bold ${
                totalVariance > 0 ? 'text-green-900' : totalVariance < 0 ? 'text-red-900' : 'text-gray-900'
              }`}>
                {totalVariance > 0 && '+'}
                {formatCurrency(reconciliation.total_variance)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Breakdown by Payment Type */}
        {reconciliation.breakdown && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Breakdown by Payment Type
            </h3>
            <div className="space-y-2">
              {Object.entries(reconciliation.breakdown).map(([type, data]) => {
                const variance = parseFloat(data.variance);
                if (variance === 0) return null;

                return (
                  <div
                    key={type}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="uppercase">
                        {type}
                      </Badge>
                      <div className="text-sm">
                        <span className="text-gray-600">EPS: </span>
                        <span className="font-medium">{formatCurrency(data.eps_claimed)}</span>
                        <span className="text-gray-600 mx-2">•</span>
                        <span className="text-gray-600">Pay Data: </span>
                        <span className="font-medium">{formatCurrency(data.pay_data)}</span>
                      </div>
                    </div>
                    <div className={`text-sm font-semibold ${
                      variance > 0 ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {variance > 0 && '+'}
                      {formatCurrency(data.variance)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recommendations */}
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
          <h4 className="font-semibold text-sm text-blue-900 mb-2">
            Recommended Actions
          </h4>
          <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
            {reconciliation.under_claimed && (
              <>
                <li>Review pay data for all statutory payments made during this period</li>
                <li>Submit corrected EPS to HMRC to claim additional amounts</li>
                <li>Contact your accountant for guidance on recovery procedures</li>
              </>
            )}
            {reconciliation.over_claimed && (
              <>
                <li>Review EPS submission for errors or duplicate claims</li>
                <li>Check if any statutory payments were reversed or cancelled</li>
                <li>Contact HMRC to discuss potential repayment options</li>
              </>
            )}
            {!hasIssues && (
              <li>Your EPS claims match your pay data - no action required</li>
            )}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

